<template>
  <div class="tile-scroller-container buttons-top">
    <div ref="tileScrollerWrapper" class="tile-scroller-wrapper">
      <div ref="tileScrollerElement" class="tile-scroller" :style="{ transition: 'all 0.5s ease', transform: `translate(${translate})` }" @transitionend="handleTransitionEnd" @mouseleave="handleMouseUp" @mousedown="handleMouseDown" @mousemove="handleMouseMove" @mouseup="handleMouseUp">
        <div class="landing-tile" v-for="(data, index) in itemList" :key="index">
          <div class="bordered-item border-two">
            <div class="bordered-item-wrapper">
              <div class="landing-tile-container">
                <div class="landing-tile-image">
                  <div class="landing-tile-image-wrapper">
                    <img v-if="data.TileImageUrl" :src="data.TileImageUrl" :alt="data.TileImageAltText" />
                  </div>
                  <div class="landing-tile-logo" v-if="data.TileLogoUrl">
                    <img :src="data.TileLogoUrl" :alt="data.TileLogoAltText" />
                  </div>
                </div>
                <div class="landing-tile-content-wrapper">
                  <h3 class="landing-tile-title" v-html="data.TileTitle"></h3>
                  <div class="landing-tile-content" v-html="data.TileContent"></div>
                </div>
                <div class="landing-tile-buttons d-flex flex-v-centre">
                  <div class="btn btn-text" v-if="data.TileModalContent" @click="showDialog(data)">Learn more</div>
                  <a class="btn" v-if="props.buttonChange == 'scroller'" v-on:click="scrollToForm()">Book now</a>
                  <a class="btn" :href="data.Url" v-else-if="props.buttonChange != 'hidden'">Book now</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="tile-scroller-controls">
        <button class="slider-prev-button" @click="prev" :disabled="isFirstLandingTileVisible"></button>
        <button class="slider-next-button" @click="next" :disabled="isLastLandingTileVisible"></button>
      </div>
    </div>
  </div>

  <Dialog v-model:visible="dialogVisible" modal dismissableMask :style="{ width: '50rem' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
    <template #container="{ closeCallback }">
      <div v-if="dialogItem" class="dialog-wrapper">
        <div class="bordered-item">
          <div class="bordered-item-wrapper">
            <Button type="button" label="" severity="secondary" @click="closeCallback"></Button>
            <div v-html="dialogItem.TileModalContent" style="height: 100%"></div>
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
  import { computed, nextTick, onMounted, ref } from 'vue';
  import { useResizeObserver } from '@vueuse/core';
  import { first, last } from 'lodash';
  import Button from 'primevue/button';
  import Dialog from 'primevue/dialog';

  interface LandingPageTileItemModel {
    TileImageUrl?: string;
    TileImageAltText?: string;
    TileLogoUrl?: string;
    TileLogoAltText?: string;
    TileTitle: string;
    TileContent: string;
    TileModalContent?: string;
    Url: string;
  }

  const props = defineProps({
    items: {
      type: String,
      required: true,
    },
    buttonChange: String
  });

  const tileScrollerElement = ref<HTMLDivElement>(null!);
  const tileScrollerWrapper = ref<HTMLDivElement>(null!);
  const firstLandingTile = ref<HTMLDivElement>(null!);
  const lastLandingTile = ref<HTMLDivElement>(null!);
  const isFirstLandingTileVisible = ref<boolean>(true);
  const isLastLandingTileVisible = ref<boolean>(false);

  const tileIndex = ref(0);
  const tileWidth = ref(0);
  const tileWidthMargin = ref(20); // add value pixels to scroll extra, eg margin between tiles
  const translate = computed(() => (tileIndex.value === 0 ? '0' : `${(tileWidth.value + tileWidthMargin.value) * -tileIndex.value}px`));
  const isTranslating = ref<boolean>(false);

  const dialogVisible = ref<boolean>(false);
  const dialogItem = ref<LandingPageTileItemModel>();
  const itemList = ref<LandingPageTileItemModel[]>([]);

  const isDragging = ref(false);
  const startX = ref(0);

	function handleMouseDown(e: MouseEvent) {
    isDragging.value = true;
    startX.value = e.clientX;
	}

	function handleMouseMove(e: MouseEvent) {
    if(!isDragging.value) return;

    if (isDragging.value == true && e.pageX < startX.value) {
			if (!isLastLandingTileVisible.value && window.innerWidth > 1023) {
				next();
			}
		} else if (isDragging.value == true && e.pageX > startX.value) {
			if (!isFirstLandingTileVisible.value && window.innerWidth > 1023) {
				prev();
			}
		}
    isDragging.value = false;
	}

  function handleMouseUp(_e: MouseEvent) {
    isDragging.value = false;
	}

  function showDialog(item: LandingPageTileItemModel) {
    dialogItem.value = item;
    dialogVisible.value = true;
  }

  function scrollToForm() {
    let yay = document.getElementById('BookingForm');
		yay?.scrollIntoView({ behavior: 'smooth' });
	}

  function prev() {
    if (!isTranslating.value) {
      tileIndex.value = tileIndex.value - 1;
      isTranslating.value = true;
    }
  }
  function next() {
    if (!isTranslating.value) {
      tileIndex.value = tileIndex.value + 1;
      isTranslating.value = true;
    }
  }

  useResizeObserver(tileScrollerElement, () => {
    const landingTile = tileScrollerElement.value.querySelector('.landing-tile') as HTMLDivElement;
    tileWidth.value = landingTile?.offsetWidth ?? 0;
  });

  function handleTransitionEnd(e: TransitionEvent) {
    if (e.propertyName === 'transform' && isTranslating.value) {
      if (firstLandingTile.value) {
        const rect = firstLandingTile.value.getBoundingClientRect();
        const startRect = tileScrollerWrapper.value.getBoundingClientRect().left;
        isFirstLandingTileVisible.value = rect.left >= startRect;
      }
      if (lastLandingTile.value) {
        const rect = lastLandingTile.value.getBoundingClientRect();
        const endRect = tileScrollerWrapper.value.getBoundingClientRect().right;
        isLastLandingTileVisible.value = rect.right <= endRect;
      }
      isTranslating.value = false;
    }
  }

  onMounted(async () => {
    itemList.value = JSON.parse(props.items);

    await nextTick();
    const landingTiles = tileScrollerElement.value.querySelectorAll('.landing-tile');

    firstLandingTile.value = first(landingTiles) as HTMLDivElement;
    lastLandingTile.value = last(landingTiles) as HTMLDivElement;
  });
</script>
